'use client';

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import type { Route } from "next";
import Image from "next/image";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronDown,
    faDownload,
    faHeart,
    faHouse,
    faPalette,
    faRightFromBracket,
    faStar,
    faWallet,
    faXmark
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { Badge } from "@/components/ui/Badge";
import { Switch } from "@/components/ui/Switch";
import { useUser } from "@/context/UserContext";
import { resolveMediaUrl } from "@/lib/media";
import { getDownloadLimits, isPremiumPlan } from "@/constants/plans";
import type { PlanCode } from "@/constants/plans";
import { BrandOnboardingModal } from "@/components/onboarding";

const WHATSAPP_NUMBER = process.env.NEXT_PUBLIC_WHATSAPP_PHONE ?? "";

const accountLinks = [
    { href: "/conta", label: "Minha conta", icon: faHouse },
    { href: "/conta/cobranca", label: "Cobrança", icon: faWallet },
    { href: "/conta/downloads", label: "Downloads", icon: faDownload },
    { href: "/conta/curtidas", label: "Curtidas", icon: faHeart },
    { href: "/conta/favoritos", label: "Favoritos", icon: faStar }
] as const;

export function UserMenu() {
    const { user, logout, isRefreshing } = useUser();
    const [open, setOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [darkMode, setDarkMode] = useState(false);
    const [showBrandModal, setShowBrandModal] = useState(false);

    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const closeMenu = () => {
        setIsClosing(true);
        setTimeout(() => {
            setOpen(false);
            setIsClosing(false);
        }, 500);
    };

    const handleOpenBrandModal = () => {
        closeMenu();
        // Small delay to allow menu animation to complete
        setTimeout(() => setShowBrandModal(true), 300);
    };

    const toggleMenu = () => {
        if (open) {
            closeMenu();
        } else {
            setOpen(true);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!open) {
                return;
            }

            if (
                menuRef.current &&
                triggerRef.current &&
                !menuRef.current.contains(event.target as Node) &&
                !triggerRef.current.contains(event.target as Node)
            ) {
                closeMenu();
            }
        };

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                closeMenu();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keyup", handleEscape);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keyup", handleEscape);
        };
    }, [open]);

    const downloadsBadge = useMemo(() => {
        if (!user) {
            return undefined;
        }

        const planCode = (user.currentPlanCode || "free") as PlanCode;
        const limits = getDownloadLimits(planCode);
        const freeUsed = user.freeDownloadsToday || 0;
        const premiumUsed = user.premiumDownloadsToday || 0;
        
        // Usuários premium têm downloads ilimitados
        const isPremium = user.isPremium || isPremiumPlan(planCode);
        const isUnlimited = limits.free >= 999999 || limits.premium >= 999999 || isPremium;

        return {
            free: isUnlimited ? "Ilimitados" : `${freeUsed}/${limits.free}`,
            premium: isUnlimited ? "Ilimitados" : `${premiumUsed}/${limits.premium}`,
            total: isUnlimited ? "Ilimitados" : `${freeUsed + premiumUsed}/${limits.total}`,
            isPremiumPlan: isPremium,
            isUnlimited
        };
    }, [user]);
    const fallbackAvatar = "/images/user/defaultimageuser.png";
    const resolvedAvatar = resolveMediaUrl(user?.avatarUrl);
    const avatarSource = resolvedAvatar ?? fallbackAvatar;
    const bypassOptimization = avatarSource.startsWith("http") || avatarSource.startsWith("blob:");

    if (!user) {
        return null;
    }

    return (
        <div className="relative">
            <button
                ref={triggerRef}
                type="button"
                onClick={toggleMenu}
                className="flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-2 py-1 pr-3 text-left text-white shadow-soft transition hover:bg-white/15"
                aria-haspopup="menu"
                aria-expanded={open}
            >
                <div className="relative h-10 w-10 flex-shrink-0 overflow-hidden rounded-full border border-white/20 bg-gradient-to-br from-brand-primary/40 to-brand-secondary/40">
                    <Image
                        src={avatarSource}
                        alt={user.name}
                        fill
                        sizes="40px"
                        className="object-cover"
                        unoptimized={bypassOptimization}
                    />
                </div>
                <FontAwesomeIcon icon={faChevronDown} className={clsx("h-3 w-3 transition", open && "rotate-180")} />
            </button>

            {open ? (
                <div
                    ref={menuRef}
                    role="menu"
                    className={clsx(
                        "fixed inset-0 z-[9000] flex h-screen w-screen flex-col overflow-y-auto bg-[#0F011B] p-6 sm:absolute sm:inset-auto sm:h-auto sm:w-80 sm:-right-14 sm:mt-4 sm:block sm:max-h-[520px] sm:overflow-y-auto sm:rounded-3xl sm:border sm:border-white/10 sm:bg-[#15032b]/95 sm:backdrop-blur-2xl sm:p-5 sm:shadow-soft lg:right-0 animate__animated animate__faster",
                        isClosing ? "animate__slideOutUp" : "animate__slideInDown"
                    )}
                >
                    <div className="mb-6 flex items-center justify-between sm:hidden">
                        <span className="text-lg font-bold text-white">Menu do usuário</span>
                        <button
                            onClick={closeMenu}
                            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/10 text-white transition hover:bg-white/20"
                        >
                            <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
                        </button>
                    </div>
                    <div className="space-y-4">
                        <div className="flex flex-col items-start gap-4 rounded-2xl border border-white/10 bg-white/5 p-4">
                            <div className="flex w-full items-center gap-3">
                                <div className="relative h-14 w-14 flex-shrink-0 overflow-hidden rounded-full border border-white/10 bg-white/10">
                                    <Image
                                        src={avatarSource}
                                        alt={user.name}
                                        fill
                                        sizes="56px"
                                        className="object-cover"
                                        unoptimized={bypassOptimization}
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <span className="block text-sm font-semibold text-white truncate">{user.name}</span>
                                    <p className="text-xs text-white/60 truncate">{user.email}</p>
                                </div>
                            </div>
                            <Badge tone="brand" className="tracking-[0.35em]">
                                {user.currentPlanName || user.membership || "Membro Grátis"}
                            </Badge>

                            {downloadsBadge && (
                                <div className="w-full space-y-2 rounded-xl border border-white/5 bg-white/5 p-3 text-xs">
                                    {downloadsBadge.isUnlimited ? (
                                        <div className="flex items-center justify-between">
                                            <span className="text-white/60">Downloads</span>
                                            <span className="font-semibold text-brand-secondary">Ilimitados ∞</span>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="flex items-center justify-between">
                                                <span className="text-white/60">Downloads Grátis Hoje</span>
                                                <span className="font-semibold text-emerald-400">{downloadsBadge.free}</span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-white/60">Downloads Premium Hoje</span>
                                                <span className="font-semibold text-[#A020F0]">{downloadsBadge.premium}</span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                            <div className="space-y-1">
                                <span className="text-sm font-semibold text-white">Modo escuro</span>
                            </div>
                            <Switch checked={darkMode} onChange={setDarkMode} label="Alternar modo escuro" />
                        </div>

                        <nav className="space-y-1" aria-label="Menu da conta">
                            {/* Minha conta link */}
                            <Link
                                href="/conta"
                                className="flex items-center justify-between rounded-2xl border border-transparent px-4 py-3 text-sm font-medium text-white/80 transition hover:border-white/15 hover:bg-white/5 hover:text-white"
                                onClick={closeMenu}
                            >
                                <span className="flex items-center gap-3">
                                    <FontAwesomeIcon icon={faHouse} className="h-4 w-4" />
                                    <span>Minha conta</span>
                                </span>
                            </Link>

                            {/* Marca button */}
                            <button
                                type="button"
                                onClick={handleOpenBrandModal}
                                className="flex w-full items-center justify-between rounded-2xl border border-transparent px-4 py-3 text-sm font-medium text-white/80 transition hover:border-white/15 hover:bg-white/5 hover:text-white"
                            >
                                <span className="flex items-center gap-3">
                                    <FontAwesomeIcon icon={faPalette} className="h-4 w-4" />
                                    <span>Marca</span>
                                </span>
                            </button>

                            {/* Other account links */}
                            {accountLinks.filter(item => item.href !== "/conta").map((item) => (
                                <Link
                                    key={item.href}
                                    href={item.href as Route}
                                    className="flex items-center justify-between rounded-2xl border border-transparent px-4 py-3 text-sm font-medium text-white/80 transition hover:border-white/15 hover:bg-white/5 hover:text-white"
                                    onClick={closeMenu}
                                >
                                    <span className="flex items-center gap-3">
                                        <FontAwesomeIcon icon={item.icon} className="h-4 w-4" />
                                        <span>{item.label}</span>
                                    </span>
                                    {item.href === "/conta/downloads" && downloadsBadge ? (
                                        <Badge tone="neutral" className="tracking-[0.1em] text-[10px]">
                                            {downloadsBadge.total}
                                        </Badge>
                                    ) : null}
                                </Link>
                            ))}

                            <a
                                href={`https://api.whatsapp.com/send?phone=${WHATSAPP_NUMBER}`}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center justify-between rounded-2xl border border-transparent px-4 py-3 text-sm font-medium text-white/80 transition hover:border-white/15 hover:bg-white/5 hover:text-white"
                            >
                                <span className="flex flex-col gap-1">
                                    <span className="flex items-center gap-3">
                                        <FontAwesomeIcon icon={faWhatsapp} className="h-4 w-4" />
                                        Suporte por WhatsApp
                                    </span>
                                    <span className="text-xs font-normal text-white/50">Dúvidas e perguntas</span>
                                </span>
                            </a>
                        </nav>

                        <div className="space-y-2">
                            <button
                                type="button"
                                onClick={() => {
                                    logout();
                                    closeMenu();
                                }}
                                className="flex w-full items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
                            >
                                <FontAwesomeIcon icon={faRightFromBracket} className="h-4 w-4" />
                                Sair
                            </button>
                            {isRefreshing ? (
                                <p className="text-center text-[11px] uppercase tracking-[0.4em] text-white/40">
                                    Atualizando dados...
                                </p>
                            ) : null}
                        </div>
                    </div>
                </div>
            ) : null}

            {/* Brand Configuration Modal */}
            <BrandOnboardingModal
                isOpen={showBrandModal}
                onComplete={() => setShowBrandModal(false)}
                onDismiss={() => setShowBrandModal(false)}
                skipWelcome
            />
        </div>
    );
}
